/**
 * Pure helpers shared across the harness group: formatting, language
 * detection, attachment constraints, and defensive value summarization.
 * No DOM access; safe to import from anywhere including tests.
 */
import type { IHarnessTodoItem } from './interfaces.js';

export const harnessMaxAttachmentBytes = 10 * 1024 * 1024;
export const harnessMaxTotalAttachmentBytes = 30 * 1024 * 1024;

export const harnessSupportedImageTypes = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
]);

export const harnessSupportedTextTypes = new Set([
  'application/csv',
  'application/json',
  'application/ld+json',
  'application/xml',
  'application/yaml',
  'text/csv',
  'text/html',
  'text/javascript',
  'text/markdown',
  'text/plain',
  'text/xml',
  'text/yaml',
]);

const mediaTypeByExtension: Record<string, string> = {
  css: 'text/css',
  csv: 'text/csv',
  html: 'text/html',
  jpeg: 'image/jpeg',
  jpg: 'image/jpeg',
  js: 'text/javascript',
  json: 'application/json',
  jsonl: 'application/json',
  log: 'text/plain',
  md: 'text/markdown',
  mjs: 'text/javascript',
  png: 'image/png',
  svg: 'image/svg+xml',
  ts: 'text/plain',
  tsx: 'text/plain',
  txt: 'text/plain',
  webp: 'image/webp',
  xml: 'application/xml',
  yaml: 'application/yaml',
  yml: 'application/yaml',
};

const languageByExtension: Record<string, string> = {
  c: 'c',
  cjs: 'javascript',
  cpp: 'cpp',
  cs: 'csharp',
  css: 'css',
  go: 'go',
  h: 'c',
  html: 'html',
  java: 'java',
  js: 'javascript',
  json: 'json',
  jsx: 'javascript',
  kt: 'kotlin',
  md: 'markdown',
  mjs: 'javascript',
  php: 'php',
  py: 'python',
  rb: 'ruby',
  rs: 'rust',
  sh: 'bash',
  sql: 'sql',
  swift: 'swift',
  toml: 'ini',
  ts: 'typescript',
  tsx: 'typescript',
  xml: 'xml',
  yaml: 'yaml',
  yml: 'yaml',
  zsh: 'bash',
};

export const harnessIsRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
};

export const harnessFormatBytes = (bytes: unknown): string => {
  const value = typeof bytes === 'number' && Number.isFinite(bytes) ? bytes : 0;
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(value < 10 * 1024 ? 1 : 0)} KB`;
  return `${(value / 1024 / 1024).toFixed(value < 10 * 1024 * 1024 ? 1 : 0)} MB`;
};

export const harnessFormatDuration = (startedAt?: number, finishedAt?: number): string => {
  if (!startedAt || !finishedAt || finishedAt < startedAt) return '';
  const ms = finishedAt - startedAt;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
};

export const harnessFormatRelativeTime = (epochMs?: number): string => {
  if (!epochMs) return '';
  const deltaMs = Date.now() - epochMs;
  if (deltaMs < 45_000) return 'just now';
  const minutes = Math.round(deltaMs / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(epochMs).toLocaleDateString();
};

export const harnessLanguageForPath = (filePath: unknown): string => {
  const extension = String(filePath || '').split('.').pop()?.toLowerCase() || '';
  return languageByExtension[extension] || 'plaintext';
};

export const harnessMediaTypeForFile = (file: File): string => {
  if (file.type) return file.type;
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  return mediaTypeByExtension[extension] || 'application/octet-stream';
};

export const harnessIsSupportedAttachmentType = (mediaType: string): boolean => {
  return (
    harnessSupportedImageTypes.has(mediaType)
    || mediaType.startsWith('text/')
    || harnessSupportedTextTypes.has(mediaType)
  );
};

export const harnessFileToBase64 = (file: File): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const marker = ';base64,';
      const markerIndex = result.indexOf(marker);
      resolve(markerIndex === -1 ? result : result.slice(markerIndex + marker.length));
    };
    reader.onerror = () => reject(reader.error || new Error(`Could not read ${file.name || 'file'}.`));
    reader.readAsDataURL(file);
  });

export const harnessTruncate = (text: string, maxChars = 6000): { text: string; truncated: boolean } => {
  if (text.length <= maxChars) return { text, truncated: false };
  return { text: text.slice(0, maxChars), truncated: true };
};

export const harnessStringifyValue = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, undefined, 2);
  } catch {
    return String(value);
  }
};

/**
 * Defensive one-glance summary of an arbitrary tool input/output object:
 * well-known keys first, then remaining scalars. Ported from the git.zone
 * IDE's agent formatting layer.
 */
/**
 * Extracts todo items from a todo-tool input/output. Accepts { todos: [...] }
 * or a bare array; items may carry content/subject/title and unknown statuses
 * normalize to 'pending'.
 */
export const harnessParseTodos = (value: unknown): IHarnessTodoItem[] => {
  const rawList = Array.isArray(value)
    ? value
    : harnessIsRecord(value) && Array.isArray(value.todos)
      ? value.todos
      : [];
  const result: IHarnessTodoItem[] = [];
  for (const entry of rawList) {
    if (typeof entry === 'string') {
      result.push({ content: entry, status: 'pending' });
      continue;
    }
    if (!harnessIsRecord(entry)) continue;
    const content = String(entry.content ?? entry.subject ?? entry.title ?? '').trim();
    if (!content) continue;
    const status = entry.status === 'completed' || entry.status === 'in_progress' ? entry.status : 'pending';
    result.push({
      id: entry.id !== undefined ? String(entry.id) : undefined,
      content,
      status,
    });
  }
  return result;
};

export const harnessSummarizeValue = (value: unknown): string => {
  if (!harnessIsRecord(value)) return harnessStringifyValue(value);
  const lines: string[] = [];
  const seenKeys = new Set<string>();
  for (const key of ['description', 'command', 'workdir', 'cwd', 'filePath', 'path', 'pattern', 'query', 'url', 'action']) {
    const item = value[key];
    if (typeof item === 'string' && item) {
      lines.push(`${key}: ${item}`);
      seenKeys.add(key);
    }
  }
  for (const [key, item] of Object.entries(value)) {
    if (seenKeys.has(key) || item === undefined || item === null) continue;
    if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
      const text = String(item);
      lines.push(`${key}: ${text.length > 240 ? `${text.slice(0, 240)}…` : text}`);
    }
  }
  return lines.join('\n');
};

/** First meaningful line of a summarized value; used for card subtitles. */
export const harnessSummaryLine = (value: unknown): string => {
  return harnessSummarizeValue(value).split('\n')[0] || '';
};
